import React, { useCallback, useEffect, useState } from "react";

import { Button, Col, Form, Modal, Row } from "react-bootstrap";

import { isValidPhoneNumber } from "libphonenumber-js";
import { Control, Controller, FieldErrors, useForm } from "react-hook-form";

import { InviteUserInput, useGetAccountDetailsByParticipantIdQuery, useInviteUserMutation } from "@api/graphql";
import { ToastMessage } from "@components/ToastMessage";
import { FormErrorText } from "@components/form/FormErrorText";
import { Loading } from "@components/loading";
import { E164_MOBILE_NUMBER_FORMAT, EMAIL } from "@util/regex";

interface ContactDetailsPropType {
  control: Control<InviteUserInput>;
  errors: FieldErrors<InviteUserInput>;
  loading: boolean;
  onCancel: () => void;
}

const UserDetails = ({ control, errors, loading, onCancel }: ContactDetailsPropType) => (
  <>
    <Row className="mb-4">
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>Title</Form.Label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Form.Control {...field} as="select" className={field.value ? "custom-select-value-selected" : ""}>
                <option hidden value="">
                  Select your title
                </option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
              </Form.Control>
            )}
          />
        </Form.Group>
      </Col>
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>* First name</Form.Label>
          <Controller
            name="first_name"
            control={control}
            rules={{ required: "First name is required" }}
            render={({ field }) => (
              <Form.Control
                {...field}
                type="text"
                placeholder="Enter your first name"
                isInvalid={!!errors?.first_name}
              />
            )}
          />
          <FormErrorText error={errors?.first_name} />
        </Form.Group>
      </Col>
    </Row>

    <Row className="mb-4">
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>Middle name</Form.Label>
          <Controller
            name="middle_name"
            control={control}
            render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your middle name" />}
          />
        </Form.Group>
      </Col>
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>* Last name</Form.Label>
          <Controller
            name="last_name"
            control={control}
            rules={{ required: "Last name is required" }}
            render={({ field }) => (
              <Form.Control {...field} type="text" placeholder="Enter your last name" isInvalid={!!errors?.last_name} />
            )}
          />
          <FormErrorText error={errors?.last_name} />
        </Form.Group>
      </Col>
    </Row>

    <Row className="mb-4">
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>Preferred pronoun</Form.Label>
          <Controller
            name="pronouns"
            control={control}
            render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your preferred pronoun" />}
          />
        </Form.Group>
      </Col>
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>Preferred name</Form.Label>
          <Controller
            name="preferred_name"
            control={control}
            render={({ field }) => (
              <Form.Control
                {...field}
                type="text"
                placeholder="Enter your preferred name"
                isInvalid={!!errors?.preferred_name}
              />
            )}
          />
        </Form.Group>
      </Col>
    </Row>

    <Row className="mb-4">
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>* Email</Form.Label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: EMAIL,
                message: "Invalid email address",
              },
            }}
            render={({ field }) => (
              <Form.Control {...field} type="text" placeholder="Enter email" isInvalid={!!errors?.email} />
            )}
          />
          <FormErrorText error={errors?.email} />
        </Form.Group>
      </Col>
      <Col md={6} sm={12}>
        <Form.Group>
          <Form.Label>Mobile number</Form.Label>
          <Form.Text className="text-muted d-block mb-2" style={{ fontSize: "0.75rem" }}>
            Enter a mobile phone number, including the country code for example +61412345678
          </Form.Text>
          <Controller
            name="phone_number"
            control={control}
            rules={{
              validate: (value) => {
                const errorMessage = `Oops, that number isn't in the right format. Please start with a plus sign '+' and add the country code ('61' for Australia), omit the first '0', then write the mobile number (no spaces)`;
                try {
                  if (value && (!E164_MOBILE_NUMBER_FORMAT.test(value) || !isValidPhoneNumber(value))) {
                    return errorMessage;
                  }
                  return true;
                } catch (error) {
                  return errorMessage;
                }
              },
            }}
            render={({ field }) => (
              <Form.Control
                {...field}
                style={{ paddingTop: "0.75rem", paddingBottom: "0.75rem" }}
                type="string"
                step={1}
                min="0"
                placeholder="Enter mobile number"
                value={field.value || "+61"} // use +61 as the default value
                isInvalid={!!errors?.phone_number}
                onWheel={(event) => event.currentTarget.blur()}
              />
            )}
          />
          <FormErrorText error={errors?.phone_number} />
        </Form.Group>
      </Col>
    </Row>
    <Row className="mb-4">
      <Col sm={12}>
        <Form.Group>
          <Form.Label>* Address</Form.Label>
          <Controller
            name="address"
            control={control}
            rules={{
              required: "Address is required",
            }}
            render={({ field }) => (
              <Form.Control {...field} type="text" placeholder="Enter address" isInvalid={!!errors?.address} />
            )}
          />
          <FormErrorText error={errors?.address} />
        </Form.Group>
      </Col>
    </Row>

    {loading && <Loading />}
    <Row className="mb-4">
      <Col className="d-flex justify-content-center mb-4 mt-4">
        <Button type="submit" variant="primary" className="me-2">
          Send email
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </Col>
    </Row>
  </>
);

export interface InviteParticipantModalProp {
  isOpen: boolean;
  onClose: () => void;
  fullName: string;
  participantId?: number;
}

export const InviteParticipantModal = ({ isOpen, onClose, fullName, participantId }: InviteParticipantModalProp) => {
  const { data, loading } = useGetAccountDetailsByParticipantIdQuery({
    variables: { participantId: participantId ?? -1 },
    skip: !participantId,
  });

  const userDetails = data?.getAccountDetailsByParticipantId.contact;
  const address = data?.getAccountDetailsByParticipantId.account?.address?.address_line_1;

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<InviteUserInput>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: { ...userDetails },
  });
  const [inviteUserMutation, { loading: isSubmitting }] = useInviteUserMutation({});
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Refetch main page participant info
  const { refetch } = useGetAccountDetailsByParticipantIdQuery({
    variables: { participantId: participantId ?? -1 },
    skip: !participantId,
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        ...userDetails,
        address: address ?? "",
      });
    }
  }, [address, isOpen, reset, userDetails]);

  const submitHandler = useCallback(
    async (data: InviteUserInput) => {
      try {
        const res = await inviteUserMutation({
          variables: {
            input: {
              title: data.title,
              first_name: data.first_name,
              last_name: data.last_name,
              middle_name: data.middle_name,
              phone_number: data.phone_number,
              preferred_name: data.preferred_name,
              pronouns: data.pronouns,
              email: data.email,
              participant_id: participantId!,
              address: data.address,
            },
          },
        });
        if (res?.data?.inviteUser?.success) {
          setToastMessage("User details updated successfully.");
          setShowToast(true);
          setIsSuccess(true);
          onClose();
          refetch();
        } else {
          setToastMessage(res.data?.inviteUser?.message || "Error updating user details");
          setShowToast(true);
          setIsSuccess(false);
        }
      } catch (error) {
        setToastMessage(`Error updating user details ${error}`);
        setShowToast(true);
        setIsSuccess(false);
      }
    },
    [inviteUserMutation, onClose, participantId, refetch],
  );

  if (!participantId) {
    return (
      <>
        <Modal show={isOpen} size="lg" onHide={onClose}>
          <Modal.Header closeButton className="p-5"></Modal.Header>
          <Modal.Body className="p-5">
            <>No participant id detected</>
          </Modal.Body>
        </Modal>
      </>
    );
  }
  return (
    <>
      <ToastMessage
        message={toastMessage}
        show={showToast}
        setShow={setShowToast}
        type={isSuccess ? "success" : "error"}
      />
      <Modal show={isOpen} size="lg" onHide={onClose}>
        <Modal.Header closeButton className="p-5">
          <h3>Invite user - {fullName}</h3>
        </Modal.Header>
        <Modal.Body className="p-5">
          <Form onSubmit={handleSubmit(submitHandler)}>
            <UserDetails control={control} errors={errors} loading={loading || isSubmitting} onCancel={onClose} />
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};
